import React, { useState, useRef, useEffect } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import gsap from 'gsap'
import PropTypes from 'prop-types'

// Components
import AnimateScaleIn from '../animation/AnimateScaleIn'

// Hooks
import { useScrolledAnimation } from '../animation/useScrolledAnimation'

// Vars
import { Heading1 } from '../TextStyles'

// Styles
import {
  SummaryStatMain,
  SummaryStatText,
  SummaryStatImage,
  SummaryStatImageInner,
  SummaryStatSpacer,
  SummaryStatSpan,
  SummaryStatLine,
} from './index.style'

const SummaryStat = ({ id, text1, text2, images, delay, imgOrder }) => {
  const [active, setActive] = useState(0)

  const hoverInterval = useRef(null),
    statRef = useRef(null),
    lineRef = useRef(null),
    statIn = useRef(null)

  const limit = images.length

  const nextImage = () => {
    setActive(active => (active < limit - 1 ? active + 1 : 0))
  }

  const startInterval = () => {
    stopInterval()
    hoverInterval.current = setInterval(nextImage, 500)
  }

  const stopInterval = () => {
    if (hoverInterval.current) clearInterval(hoverInterval.current)
  }

  useEffect(() => {
    return () => {
      stopInterval()
    }
  }, [])

  useScrolledAnimation({
    id: id + `-text`,
    transition: statIn,
    sync: true,
  })

  useEffect(() => {
    if (!statRef.current || !lineRef.current) return

    statIn.current = gsap.timeline({
      paused: true,
      defaults: {
        duration: 1.5,
        ease: 'power2.out',
      },
    })

    statIn.current.fromTo(
      statRef.current,
      {
        opacity: 0,
        x: 50,
      },
      {
        opacity: 1,
        x: 0,
      },
      0
    )

    statIn.current.fromTo(
      lineRef.current,
      {
        opacity: 1,
        scaleX: 0,
      },
      {
        opacity: 0.2,
        scaleX: 1,
        ease: 'power2.inOut',
      },
      0
    )
  }, [statRef, lineRef])

  return (
    <SummaryStatMain
      onMouseEnter={startInterval}
      onMouseLeave={stopInterval}
      data-scroll
      data-scroll-id={id + `-text`}
    >
      <SummaryStatLine ref={lineRef} />
      <SummaryStatText ref={statRef}>
        <Heading1>
          {images && (
            <SummaryStatImage order={imgOrder}>
              <AnimateScaleIn
                id={id + `-image`}
                delay={delay - 1}
                scaled={2}
                borderRadius={true}
              >
                {images.map((item, itemIndex) => {
                  return (
                    <SummaryStatImageInner
                      key={itemIndex}
                      visible={itemIndex === active}
                    >
                      <GatsbyImage
                        as={'span'}
                        image={item.gatsbyImageData}
                        alt={item.description}
                      />
                    </SummaryStatImageInner>
                  )
                })}
              </AnimateScaleIn>
            </SummaryStatImage>
          )}
          <SummaryStatSpan order={1}>{text1}</SummaryStatSpan>
          {text2 && (
            <>
              <SummaryStatSpacer order={2}>&nbsp;</SummaryStatSpacer>
              <SummaryStatSpan order={4}>{text2}</SummaryStatSpan>
            </>
          )}
        </Heading1>
      </SummaryStatText>
    </SummaryStatMain>
  )
}

SummaryStat.propTypes = {
  inView: PropTypes.bool,
  text1: PropTypes.string,
  text2: PropTypes.string,
  images: PropTypes.array,
  delay: PropTypes.number,
  imgOrder: PropTypes.number,
}

export default SummaryStat
